import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const GiveawayAnalytics = ({ giveawayId, onClose }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [giveawayId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/giveaways/${giveawayId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data.analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError(error.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h2>📊 Loading Analytics...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h2>❌ Error</h2>
          <p>{error}</p>
          <button onClick={onClose} className="close-btn">Close</button>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  // Prepare Entry Velocity Chart Data
  const entryVelocityData = {
    labels: analytics.entryVelocity.map(v => 
      new Date(v.timestamp).toLocaleDateString() + ' ' + 
      new Date(v.timestamp).toLocaleTimeString([], { hour: '2-digit' })
    ),
    datasets: [
      {
        label: 'Entries per Hour',
        data: analytics.entryVelocity.map(v => v.count),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4
      },
      {
        label: 'Cumulative Entries',
        data: analytics.entryVelocity.map(v => v.cumulative),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        tension: 0.4
      }
    ]
  };

  // Prepare Geographic Distribution Data
  const hasGeoData = analytics.geographicDistribution.countries.length > 0;
  
  const geoData = hasGeoData ? {
    labels: analytics.geographicDistribution.countries.map(c => c.name),
    datasets: [{
      label: 'Entries by Country',
      data: analytics.geographicDistribution.countries.map(c => c.count),
      backgroundColor: [
        'rgba(255, 99, 132, 0.8)',
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 206, 86, 0.8)',
        'rgba(75, 192, 192, 0.8)',
        'rgba(153, 102, 255, 0.8)',
        'rgba(255, 159, 64, 0.8)',
        'rgba(199, 199, 199, 0.8)',
        'rgba(83, 102, 255, 0.8)',
        'rgba(255, 99, 255, 0.8)',
        'rgba(99, 255, 132, 0.8)'
      ]
    }]
  } : null;

  // Prepare Peak Hours Data
  const peakHoursData = {
    labels: analytics.peakHours.hourlyDistribution.map(h => h.label),
    datasets: [{
      label: 'Entries by Hour (UTC)',
      data: analytics.peakHours.hourlyDistribution.map(h => h.count),
      backgroundColor: 'rgba(54, 162, 235, 0.8)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1
    }]
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '1200px', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2 style={{ margin: 0 }}>📊 Giveaway Analytics</h2>
          <button onClick={onClose} className="close-btn" style={{ background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer' }}>
            ×
          </button>
        </div>

        {/* Overview Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px', 
          marginBottom: '40px' 
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '20px',
            borderRadius: '12px',
            color: 'white'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📝</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.overview.totalEntries}</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Total Entries</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            padding: '20px',
            borderRadius: '12px',
            color: 'white'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>👥</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.overview.uniqueParticipants}</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Unique Participants</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            padding: '20px',
            borderRadius: '12px',
            color: 'white'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📈</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.overview.avgEntriesPerParticipant}</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Avg Entries/Participant</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            padding: '20px',
            borderRadius: '12px',
            color: 'white'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚡</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{analytics.overview.entryRatePerHour}</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Entries/Hour</div>
          </div>
        </div>

        {/* Entry Velocity Chart */}
        <div style={{ 
          background: 'var(--card-bg)', 
          padding: '25px', 
          borderRadius: '12px', 
          marginBottom: '30px',
          border: '1px solid var(--border)'
        }}>
          <h3 style={{ marginBottom: '20px' }}>📈 Entry Velocity Over Time</h3>
          <Line 
            data={entryVelocityData} 
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                  labels: { color: 'var(--text)' }
                },
                title: {
                  display: false
                }
              },
              scales: {
                y: {
                  ticks: { color: 'var(--text)' },
                  grid: { color: 'rgba(255,255,255,0.1)' }
                },
                x: {
                  ticks: { 
                    color: 'var(--text)',
                    maxRotation: 45,
                    minRotation: 45
                  },
                  grid: { color: 'rgba(255,255,255,0.1)' }
                }
              }
            }} 
          />
        </div>

        {/* Geographic Distribution */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '30px', 
          marginBottom: '30px' 
        }}>
          <div style={{ 
            background: 'var(--card-bg)', 
            padding: '25px', 
            borderRadius: '12px',
            border: '1px solid var(--border)'
          }}>
            <h3 style={{ marginBottom: '20px' }}>🌍 Geographic Distribution</h3>
            {hasGeoData ? (
              <>
                <Pie 
                  data={geoData} 
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: { color: 'var(--text)' }
                      }
                    }
                  }} 
                />
                <div style={{ marginTop: '20px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  <strong>{analytics.geographicDistribution.totalCountries}</strong> countries represented
                </div>
              </>
            ) : (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center', 
                color: 'var(--text-secondary)',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>🌍</div>
                <div style={{ fontSize: '1.1rem', marginBottom: '10px', fontWeight: '500' }}>
                  No Geographic Data Available
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  Location data is not yet collected from participants.
                </div>
              </div>
            )}
          </div>

          <div style={{ 
            background: 'var(--card-bg)', 
            padding: '25px', 
            borderRadius: '12px',
            border: '1px solid var(--border)'
          }}>
            <h3 style={{ marginBottom: '20px' }}>🕐 Peak Entry Times (UTC)</h3>
            <Bar 
              data={peakHoursData} 
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    ticks: { color: 'var(--text)' },
                    grid: { color: 'rgba(255,255,255,0.1)' }
                  },
                  x: {
                    ticks: { color: 'var(--text)' },
                    grid: { color: 'rgba(255,255,255,0.1)' }
                  }
                }
              }} 
            />
            <div style={{ marginTop: '15px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Peak Hours: <strong>{analytics.peakHours.peakHours.join(', ')}</strong>
            </div>
          </div>
        </div>

        {/* Requirement Completion Rates */}
        {analytics.requirementCompletion.length > 0 && (
          <div style={{ 
            background: 'var(--card-bg)', 
            padding: '25px', 
            borderRadius: '12px', 
            marginBottom: '30px',
            border: '1px solid var(--border)'
          }}>
            <h3 style={{ marginBottom: '20px' }}>✅ Requirement Completion Rates</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {analytics.requirementCompletion.map((req, index) => (
                <div key={index}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '500' }}>{req.description}</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>
                      {req.completionRate}%
                    </span>
                  </div>
                  <div style={{ 
                    height: '8px', 
                    background: 'rgba(255,255,255,0.1)', 
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      height: '100%', 
                      width: `${req.completionRate}%`, 
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '5px' }}>
                    {req.completed} of {req.attempted} participants
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Participants */}
        {analytics.topParticipants.length > 0 && (
          <div style={{ 
            background: 'var(--card-bg)', 
            padding: '25px', 
            borderRadius: '12px',
            border: '1px solid var(--border)'
          }}>
            <h3 style={{ marginBottom: '20px' }}>🏆 Top Participants</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {analytics.topParticipants.map((participant, index) => (
                <div key={participant.userId} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'var(--text-secondary)'
                  }}>
                    #{index + 1}
                  </div>
                  {participant.avatar && (
                    <img 
                      src={participant.avatar} 
                      alt={participant.username}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500' }}>{participant.username}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      {participant.entries} {participant.entries === 1 ? 'entry' : 'entries'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiveawayAnalytics;
